/**
 * Helpers for opening/downloading files via public links (URLs only, no storage).
 */

/** Check if a value is a public URL (http/https). */
export function isPublicLink(value: string | null | undefined): boolean {
  const v = value?.trim();
  return !!(v && (v.startsWith('http://') || v.startsWith('https://')));
}

/** Get the public URL from an item, picking the first URL-shaped value among
 *  the candidate fields. We can't just `??`-chain them: rows synced from
 *  Firebase storage put a bare storage path in `file_path` AND the full URL in
 *  `download_url` — a `??` chain would short-circuit on the non-URL path. */
export function getPublicUrl(item: {
  file_path?: string | null;
  url?: string | null;
  download_url?: string | null;
}): string | null {
  for (const candidate of [item.url, item.download_url, item.file_path]) {
    const v = candidate?.trim();
    if (v && (v.startsWith('http://') || v.startsWith('https://'))) return v;
  }
  return null;
}

/** Open a public link in a new tab. */
export function openPublicLink(url: string): void {
  window.open(url, '_blank');
}

/**
 * Trigger download via direct link click (no fetch). Use for cross-origin URLs
 * (e.g. Firebase Storage) where fetch would be blocked by CORS. The browser
 * will GET the URL and download or open the file.
 */
export function downloadViaLink(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Download a file from a public URL — fetches the file and triggers a real
 * browser download (save to disk) with the given filename.
 * Throws if fetch fails (e.g. CORS). Caller can fall back to opening the URL.
 */
export async function downloadFromUrl(url: string, filename: string): Promise<void> {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}
